import liff from '@line/liff'

import { API_BASE } from '../config'

/* ============================================================================
   เปิดหน้า dashboard Walk & Run ใน LINE

   ⚠️ หน้านี้อยู่โฮสต์เดียวกับหลังบ้าน (simat2026.simat.work) แต่ **ไม่ใช้ cookie
      session ของหลังบ้าน** — ยืนยันตัวตนด้วย idToken ของ LINE เหมือนฝั่ง LIFF
      ฉะนั้นห้ามพาไปหน้า /login/admin และห้ามอยู่ใต้ RequireAuth

   ⚠️ ใช้ LIFF ตัวแยก (LIFF_ID_WALHRUN) ไม่ใช่ตัวเดียวกับ liff.simat.work
      เพราะ LIFF app หนึ่งตัวตั้ง Endpoint URL ได้ url เดียว และสองหน้านี้อยู่
      คนละโฮสต์ → ต้องมี LIFF app คนละตัว
      id ดึงจาก backend ที่ /config/<บริษัท> ไม่ hardcode ลง bundle

   โครงตัวนี้ลอกแนวจาก frontend-liff/src/lib/liff.ts (ตัวนั้นมี resolveLiffId
   ของตัวเอง) — ที่นี่ตัดให้เหลือเท่าที่หน้านี้ใช้
   ============================================================================ */

export type WalkrunSession = {
  displayName: string | null
  /** ส่งไปให้ backend verify — ห้ามเชื่อ userId จาก client ตรง ๆ */
  idToken: string
  /** true = เปิดอยู่ในแอป LINE, false = เปิดใน browser ปกติ */
  inClient: boolean
}

/** บริษัทของ build นี้ — หลังบ้าน 1 deploy ผูก 1 บริษัท เหมือน AUTH_COMPANY ฝั่ง backend */
const COMPANY = 'simat'

const STEP_TIMEOUT_MS = 15_000

/**
 * ทุกขั้นต้องมีเพดานเวลา
 *
 * ของเดิมฝั่ง LIFF ไม่มี แล้วเจอจริงว่า fetch ค้างอยู่เฉย ๆ ในแอป LINE หน้าเว็บ
 * ค้างที่ "กำลังเชื่อมต่อ" ตลอดกาลโดยไม่มี error ให้เห็น — ล้มดัง ๆ ดีกว่าค้างเงียบ ๆ
 */
function withTimeout<T>(work: Promise<T>, label: string, ms = STEP_TIMEOUT_MS): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} ไม่เสร็จใน ${ms / 1000} วินาที`)), ms)
    work.then(
      (v) => {
        clearTimeout(timer)
        resolve(v)
      },
      (e) => {
        clearTimeout(timer)
        reject(e)
      },
    )
  })
}

async function resolveLiffId(): Promise<string> {
  if (!API_BASE) throw new Error('ยังไม่ได้ตั้ง VITE_API_BASE ใน .env')

  const res = await fetch(`${API_BASE}/config/${COMPANY}`)
  if (!res.ok) throw new Error(`ขอ LIFF ID จาก backend ไม่ได้ (HTTP ${res.status})`)

  const data = (await res.json()) as { walkrunLiffId?: string | null }
  if (!data.walkrunLiffId) {
    throw new Error('ยังไม่ได้ตั้ง LIFF_ID_WALHRUN ใน .env ของ backend — แจ้งผู้ดูแลระบบ')
  }
  return data.walkrunLiffId
}

/** @param onStep รายงานว่ากำลังทำขั้นไหน เวลาช้าจะรู้ว่าช้าที่ตรงไหน */
export async function initWalkrunLiff(
  onStep: (step: string) => void = () => {},
): Promise<WalkrunSession> {
  onStep('ขอ LIFF ID จาก backend…')
  const liffId = await withTimeout(resolveLiffId(), 'ขอ LIFF ID จาก backend')

  onStep('เชื่อมต่อ LIFF…')
  await withTimeout(liff.init({ liffId }), 'liff.init()')

  if (!liff.isLoggedIn()) {
    // เปิดในแอป LINE แต่ยังไม่ login = ผิดปกติ ต้องบอกให้เห็น
    // ห้ามเรียก login() ตรงนี้ เพราะในแอปมันไม่พาไปไหน แล้วจะค้างที่ promise ตลอดกาล
    if (liff.isInClient()) {
      throw new Error(
        'เปิดในแอป LINE แต่ยังไม่ได้ login — ปิดแอป LINE ให้สุด (swipe ออกจาก app switcher) แล้วเปิดใหม่',
      )
    }
    onStep('พาไป login กับ LINE…')
    liff.login({ redirectUri: window.location.href })
    // login() พาออกจากหน้าไปเลย บรรทัดถัดไปจะไม่ได้รัน — กันไม่ให้ไหลต่อ
    await new Promise<never>(() => {})
  }

  const idToken = liff.getIDToken()
  if (!idToken) throw new Error('LINE ไม่ได้ให้ idToken มา — ลองปิดแอป LINE แล้วเปิดใหม่')

  return {
    displayName: liff.getDecodedIDToken()?.name ?? null,
    idToken,
    inClient: liff.isInClient(),
  }
}
